import React from 'react';
import { Box, Typography, Button, TextField, MenuItem, ListSubheader } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { CATEGORY_OPTIONS, SORT_LABELS } from '../../constants';

interface ChatListingHeaderProps {
  category: string;
  sortBy: string;
  onCategoryChange: (category: string) => void;
  onSortChange: (sortBy: string) => void;
  onNewChat: () => void;
  onNewIdeation: () => void;
  onOpenSearch: () => void;
  isSearchOpen?: boolean;
}

// Define which values belong to each group
const IDEATION_VALUES = [
  'Current Process',
  'Expected Impact',
  'Specific Idea(s) for Improvement',
  'Key Performance Indicators',
];

const SOLUTION_VALUES = [
  'Opportunity Statement',
  'Current Process (Solution)',
  'Proposed Enhancements',
  'Estimated Impact (Solution)',
];

const subheaderSx = {
  fontFamily: 'Poppins',
  fontWeight: 600,
  fontSize: '14px',
  color: '#737373',
  lineHeight: '24px',
  letterSpacing: '0.05em',
  borderTop: '1px solid #E5E7EB',
  px: 1,
  py: 0.5,
};

const ChatListingHeader: React.FC<ChatListingHeaderProps> = ({
  category,
  sortBy,
  onCategoryChange,
  onSortChange,
  onNewChat,
  onNewIdeation,
  onOpenSearch,
  isSearchOpen = false,
}) => {
  const selectedItemSx = {
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '100%',
    letterSpacing: '-0.02em',
    py: 1,
    px: 2,
    '&.Mui-selected': {
      backgroundColor: '#ECF2FF !important',
    },
    '&.Mui-selected:hover': {
      backgroundColor: '#ECF2FF !important',
    },
    '&.Mui:hover': {
      backgroundColor: '#F6F6F6 !important',
    },
  };
  const renderGroupedCategoryOptions = () => {
    const items: React.ReactNode[] = [];

    const renderMenuItem = (option: { value: string; label: string }) => (
      <MenuItem key={option.value} value={option.value} sx={selectedItemSx}>
        {option.label}
        {option.value === category && (
          <img src="/images/blueTick.svg" alt="selected" style={{ marginLeft: 'auto' }} />
        )}
      </MenuItem>
    );

    // "All" option — ungrouped
    const allOption = CATEGORY_OPTIONS.find((o) => o.value === 'All');
    if (allOption) {
      items.push(renderMenuItem(allOption));
    }

    // Ideation group
    const ideationOptions = CATEGORY_OPTIONS.filter((o) => IDEATION_VALUES.includes(o.value));
    if (ideationOptions.length > 0) {
      items.push(
        <ListSubheader key="group-ideation" sx={subheaderSx}>
          Ideation
        </ListSubheader>
      );
      ideationOptions.forEach((option) => items.push(renderMenuItem(option)));
    }

    // Solution group
    const solutionOptions = CATEGORY_OPTIONS.filter((o) => SOLUTION_VALUES.includes(o.value));
    if (solutionOptions.length > 0) {
      items.push(
        <ListSubheader key="group-solution" sx={subheaderSx}>
          Solution
        </ListSubheader>
      );
      solutionOptions.forEach((option) => items.push(renderMenuItem(option)));
    }

    return items;
  };

  return (
    <Box sx={{ px: 2, pt: 2, pb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Poppins',
            fontWeight: 700,
            color: '#000000',
            fontSize: '24px',
          }}
        >
          Data XPLR
        </Typography>

        {/* Create New Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            sx={{
              backgroundColor: '#E4EBFF',
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              borderRadius: '10px',
              textTransform: 'capitalize',
              '&:hover': {
                backgroundColor: '#eff6ff',
              },
            }}
            onClick={() => {
              onNewChat();
              onNewIdeation();
            }}
          >
            <img src="/images/add-new.png" alt="Add" width={13} style={{ marginRight: '4px' }} />
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, fontSize: '14px', color: '#000000' }}
            >
              New
            </Typography>
          </Button>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor: isSearchOpen ? '#DDDFDE' : 'transparent',
              '&:hover': {
                backgroundColor: '#DDDFDE',
              },
              '&:active': {
                backgroundColor: '#DDDFDE',
              },
            }}
            onClick={onOpenSearch}
          >
            <img src="/images/search-icon.png" alt="Search" width={20} height={20} />
          </Box>
        </Box>
      </Box>

      <Box sx={{ mb: 1 }}>
        <TextField
          select
          fullWidth
          value={category}
          onChange={(e) => onCategoryChange(e.target.value)}
          size="small"
          SelectProps={{
            native: false,
            IconComponent: (props) => (
              <KeyboardArrowDown
                {...props}
                sx={{
                  color: '#131426 !important',
                  fontSize: 24,
                }}
              />
            ),
            renderValue: (selected: any) => {
              const displayText =
                selected && selected.length > 20 ? selected.substring(0, 20) + '...' : selected;

              return (
                <>
                  <span
                    style={{
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#737373',
                      textAlign: 'left',
                    }}
                  >
                    Category:{' '}
                  </span>
                  <span
                    style={{
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#000000',
                      textAlign: 'right',
                    }}
                  >
                    {displayText}
                  </span>
                </>
              );
            },
            MenuProps: {
              PaperProps: {
                sx: {
                  boxShadow: 'none',
                  border: '1px solid #B9B9B9',
                  borderRadius: '6px',
                  mt: 1,
                  overflow: 'hidden',
                },
              },
              MenuListProps: {
                sx: {
                  maxHeight: 'min(320px, 70vh)',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  paddingTop: 0,
                  paddingBottom: 0,
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#c7c7c7',
                    borderRadius: '4px',
                  },
                },
              },
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              fontSize: '14px',
              '& fieldset': {
                border: 'none !important',
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: '8px 12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingRight: '40px !important',
            },
          }}
        >
          {renderGroupedCategoryOptions()}
        </TextField>
      </Box>

      {/* Sort By Dropdown */}
      <Box sx={{ mb: 1 }}>
        <TextField
          select
          fullWidth
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value)}
          size="small"
          SelectProps={{
            native: false,
            IconComponent: (props) => (
              <KeyboardArrowDown
                {...props}
                sx={{
                  color: '#131426 !important',
                  fontSize: 24,
                }}
              />
            ),
            renderValue: (selected: any) => {
              const displayText = SORT_LABELS[selected] || selected;

              return (
                <>
                  <span
                    style={{
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#737373',
                      textAlign: 'left',
                    }}
                  >
                    Sort By:{' '}
                  </span>
                  <span
                    style={{
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#000000',
                      textAlign: 'right',
                    }}
                  >
                    {displayText}
                  </span>
                </>
              );
            },
            MenuProps: {
              PaperProps: {
                sx: {
                  boxShadow: 'none',
                  border: '1px solid #B9B9B9',
                  borderRadius: '6px',
                  mt: 1,
                  overflow: 'hidden',
                },
              },
              MenuListProps: {
                sx: {
                  maxHeight: 'min(320px, 70vh)',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  paddingTop: 0,
                  paddingBottom: 0,
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#c7c7c7',
                    borderRadius: '4px',
                  },
                },
              },
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              fontSize: '14px',
              '& fieldset': {
                border: 'none !important',
              },
            },

            '& .MuiOutlinedInput-input': {
              padding: '8px 12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingRight: '40px !important',
            },
          }}
        >
          <MenuItem value="newest_first" sx={selectedItemSx}>
            Newest First
            {sortBy === 'newest_first' && (
              <img src="/images/blueTick.svg" alt="selected" style={{ marginLeft: 'auto' }} />
            )}
          </MenuItem>
          <MenuItem value="oldest_first" sx={selectedItemSx}>
            Oldest First
            {sortBy === 'oldest_first' && (
              <img src="/images/blueTick.svg" alt="selected" style={{ marginLeft: 'auto' }} />
            )}
          </MenuItem>
          <MenuItem value="name_asc" sx={selectedItemSx}>
            Name Ascending (A-Z)
            {sortBy === 'name_asc' && (
              <img src="/images/blueTick.svg" alt="selected" style={{ marginLeft: 'auto' }} />
            )}
          </MenuItem>
          <MenuItem value="name_desc" sx={selectedItemSx}>
            Name Descending (Z-A)
            {sortBy === 'name_desc' && (
              <img src="/images/blueTick.svg" alt="selected" style={{ marginLeft: 'auto' }} />
            )}
          </MenuItem>
          <MenuItem value="most_intakes" sx={selectedItemSx}>
            Most used to Least
            {sortBy === 'most_intakes' && (
              <img src="/images/blueTick.svg" alt="selected" style={{ marginLeft: 'auto' }} />
            )}
          </MenuItem>
          <MenuItem value="least_intakes" sx={selectedItemSx}>
            Least used to Most
            {sortBy === 'least_intakes' && (
              <img src="/images/blueTick.svg" alt="selected" style={{ marginLeft: 'auto' }} />
            )}
          </MenuItem>
        </TextField>
      </Box>
    </Box>
  );
};

export default ChatListingHeader;
